import { billingGetStatus } from "./billingCallables";
import { fetchSelfRetailerProfile } from "./profileApi";

const normalizeString = (value) => (typeof value === "string" ? value.trim() : "");

const dedupe = (values = []) => {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const normalized = normalizeString(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(normalized);
  });
  return out;
};

const collectIdsFromValue = (value) => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(collectIdsFromValue);
  if (value && typeof value === "object") {
    return [value.id, value.retailerId, value.uid, value.docId].filter(Boolean);
  }
  return [];
};

const extractRetailerIdFromClaims = (claims = {}) => {
  const claimCandidates = [
    claims?.retailer_id,
    claims?.retailerId,
    claims?.retailer,
    claims?.retailer_admin,
    claims?.retailers,
    claims?.retailer_ids,
    claims?.retailerIds,
    claims?.retailerIDs,
    claims?.allowedRetailers,
  ];

  for (const candidate of claimCandidates) {
    if (typeof candidate === "string" && normalizeString(candidate)) {
      return normalizeString(candidate);
    }
    if (Array.isArray(candidate) && candidate.length > 0) {
      const first = normalizeString(candidate[0]);
      if (first) return first;
    }
  }
  return "";
};

export const extractRetailerIdCandidates = (profile = {}) =>
  dedupe([
    profile?.retailerId,
    profile?.pinLinkedRetailerId,
    profile?.retailer?.id,
    profile?.retailer?.retailerId,
    profile?.businessInsights?.retailerId,
    profile?.businessInsights?.basicInfo?.retailerId,
    profile?.onboarding?.retailerId,
  ]);

const extractAuthorizedRetailerIdsFromProfile = (profile = {}, userId = "") =>
  dedupe([
    userId,
    profile?.retailerId,
    ...collectIdsFromValue(profile?.retailerIds),
    ...collectIdsFromValue(profile?.retailerIDs),
    ...collectIdsFromValue(profile?.retailers),
    ...collectIdsFromValue(profile?.allowedRetailers),
    ...collectIdsFromValue(profile?.accountRetailers),
  ]);

const resolveAuthorizedCandidate = async ({ user, candidate }) => {
  const userId = normalizeString(user?.uid);
  const normalizedCandidate = normalizeString(candidate);
  if (!userId || !normalizedCandidate) return "";
  if (normalizedCandidate === userId) return normalizedCandidate;

  try {
    const profile = await fetchSelfRetailerProfile(user, { retailerId: normalizedCandidate });
    const authorizedIds = extractAuthorizedRetailerIdsFromProfile(profile || {}, userId);
    const returnedRetailerId = normalizeString(profile?.retailerId);
    if (authorizedIds.includes(normalizedCandidate) && (!returnedRetailerId || returnedRetailerId === normalizedCandidate)) {
      return normalizedCandidate;
    }
  } catch (error) {
    console.warn("[retailerIdentity] selected retailer authorization failed", {
      userId,
      candidate: normalizedCandidate,
      message: error?.message || String(error),
    });
  }

  return "";
};

export const resolveRetailerIdForUser = async ({ user, profile = {}, selectedRetailerId = "" } = {}) => {
  const userId = normalizeString(user?.uid);
  if (!userId) return "";

  const selected = normalizeString(selectedRetailerId);
  if (selected) {
    const authorizedSelected = await resolveAuthorizedCandidate({ user, candidate: selected });
    if (authorizedSelected) return authorizedSelected;
  }

  // Explicit profile IDs always win when present.
  const explicit = extractRetailerIdCandidates(profile)[0];
  if (explicit) {
    const authorizedExplicit = await resolveAuthorizedCandidate({ user, candidate: explicit });
    if (authorizedExplicit) return authorizedExplicit;
  }

  // Claims are only accepted after the same server-side retailer-scope validation.
  let claimRetailerId = "";
  try {
    const tokenResult = await user?.getIdTokenResult?.();
    claimRetailerId = extractRetailerIdFromClaims(tokenResult?.claims || {});
    if (claimRetailerId) {
      const authorizedClaim = await resolveAuthorizedCandidate({ user, candidate: claimRetailerId });
      if (authorizedClaim) return authorizedClaim;
    }
  } catch (error) {
    console.warn("[retailerIdentity] token claim lookup failed", {
      userId,
      message: error?.message || String(error),
    });
  }

  // Canonical server authority for billing/profile scope.
  try {
    const billingStatus = await billingGetStatus({ uid: userId, forceRefresh: false });
    const canonicalRetailerId = normalizeString(billingStatus?.retailerId);
    if (canonicalRetailerId) {
      return canonicalRetailerId;
    }
  } catch (error) {
    console.warn("[retailerIdentity] billingGetStatus failed", {
      userId,
      message: error?.message || String(error),
    });
  }

  // Deterministic onboarding identity: auth UID-backed retailer doc.
  return userId;
};

export default resolveRetailerIdForUser;
